import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  TextField,
  Drawer,
  IconButton,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Slider,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { useApi } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

const DRAWER_WIDTH = 320;

const FILTER_CONTROL_SX = {
  '& .MuiInputBase-root': {
    minHeight: 34,
    fontSize: '0.9rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem'
  },
  '& .MuiSelect-select': {
    py: 0.4
  }
};

const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return { start, end: now };
};

const TABLE_COLUMNS = [
  { id: 'fecha', label: 'Fecha', align: 'left', sortable: true, filterable: true },
  { id: 'agent', label: 'Agente', align: 'left', sortable: true, filterable: true },
  { id: 'typecall', label: 'Tipo', align: 'left', sortable: true, filterable: true },
  { id: 'skill', label: 'Skill', align: 'left', sortable: true, filterable: true },
  { id: 'numbercall', label: 'Número', align: 'left', sortable: true, filterable: true },
  { id: 'timecall', label: 'Duración', align: 'right', sortable: true, filterable: true },
  { id: 'timequeue', label: 'Cola', align: 'right', sortable: true, filterable: true },
  { id: 'timering', label: 'Ring', align: 'right', sortable: true, filterable: true },
  { id: 'timeacw', label: 'ACW', align: 'right', sortable: true, filterable: true },
  { id: 'timehold', label: 'Hold', align: 'right', sortable: true, filterable: true },
  { id: 'resultdesc', label: 'Resultado', align: 'left', sortable: true, filterable: true },
  { id: 'trunk', label: 'Trunk', align: 'left', sortable: true, filterable: true },
  { id: 'call_id', label: 'Call ID', align: 'left', sortable: true, filterable: true },
  { id: 'hangupcall', label: 'hangupcall', align: 'center', sortable: true, filterable: true },
  { id: 'actions', label: 'Acciones', align: 'center', sortable: false, filterable: false }
];

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

const formatHangupcall = (value) => {
  if (value === null || value === undefined) return '-';
  const normalized = Number(value);
  if (normalized === 1) return 'Agente';
  if (normalized === 0) return 'Usuario';
  return String(value);
};

const CallLogsPage = () => {
  const { apiCall } = useApi();
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  // Estados principales
  const [loading, setLoading] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [stats, setStats] = useState(null);
  // const [filterValues, setFilterValues] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerExpanded, setDrawerExpanded] = useState(true);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Nuevos estados para tabla
  const [tableZoom, setTableZoom] = useState(100);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ column: 'fecha', direction: 'desc' });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [zoomAnchorEl, setZoomAnchorEl] = useState(null);

  // Estados para modal de detalle y reproductor
  const [selectedCall, setSelectedCall] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState('');

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Filtros
  const [filters, setFilters] = useState(() => {
    const { start, end } = getDefaultDateRange();
    return {
      dateFrom: start,
      dateTo: end,
      agent: '',
      typecall: '',
      skill: '',
      numbercall: '',
      trunk: '',
      call_id: '',
      resultdesc: '',
      hangupcall: '',
      minDuration: '',
      maxDuration: '',
      sortBy: 'fecha',
      sortOrder: 'DESC'
    };
  });

  const getColumnSortValue = (log, columnId) => {
    switch (columnId) {
      case 'fecha': {
        if (!log.fecha) return 0;
        const dateValue = new Date(log.fecha).getTime();
        return Number.isNaN(dateValue) ? 0 : dateValue;
      }
      case 'agent':
      case 'typecall':
      case 'skill':
      case 'numbercall':
      case 'resultdesc':
      case 'trunk':
      case 'call_id':
        return (log[columnId] || '').toString().toLowerCase();
      case 'timecall':
      case 'timequeue':
      case 'timering':
      case 'timeacw':
      case 'timehold':
        return Number(log[columnId]) || 0;
      case 'hangupcall':
        if (log.hangupcall === null || log.hangupcall === undefined) return -1;
        return Number(log.hangupcall) || 0;
      default:
        return (log[columnId] || '').toString().toLowerCase();
    }
  };

  const getColumnFilterValue = (log, columnId) => {
    if (!log) return '';
    switch (columnId) {
      case 'fecha': {
        if (!log.fecha) return '';
        try {
          return format(new Date(log.fecha), 'dd/MM/yyyy HH:mm:ss');
        } catch (error) {
          return log.fecha.toString();
        }
      }
      case 'typecall':
      case 'skill':
      case 'agent':
      case 'numbercall':
      case 'resultdesc':
      case 'trunk':
      case 'call_id':
        return log[columnId] ? String(log[columnId]) : '';
      case 'timecall':
      case 'timequeue':
      case 'timering':
      case 'timeacw':
      case 'timehold':
        return log[columnId] !== null && log[columnId] !== undefined
          ? String(log[columnId])
          : '';
      case 'hangupcall':
        return log.hangupcall !== null && log.hangupcall !== undefined
          ? String(log.hangupcall)
          : '';
      default:
        return log[columnId] ? String(log[columnId]) : '';
    }
  };

  const handleSortChange = (columnId) => {
    setSortConfig((prev) => {
      if (prev.column === columnId) {
        return {
          column: columnId,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { column: columnId, direction: 'asc' };
    });
  };

  const handleColumnFilterChange = (columnId, value) => {
    if (!columnId) return;
    setColumnFilters((prev) => {
      if (!value) {
        const { [columnId]: _omit, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [columnId]: value
      };
    });
  };

  const handleToggleColumnFilter = (columnId) => {
    setActiveFilterColumn((prev) => (prev === columnId ? null : columnId));
  };

  const handleZoomIn = () => {
    setTableZoom((prev) => Math.min(prev + 10, 150));
  };

  const handleZoomOut = () => {
    setTableZoom((prev) => Math.max(prev - 10, 50));
  };

  const handleZoomReset = () => {
    setTableZoom(100);
  };

  // Funciones para el modal de detalle
  const handleViewDetails = useCallback((call) => {
    setSelectedCall(call);
    setModalOpen(true);
    setTranscriptData(null);
    setTranscriptError('');
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCall(null);
    setTranscriptData(null);
    setTranscriptError('');
    setTranscriptLoading(false);
  };

  // Generar URL de la grabación
  const getRecordingUrl = useCallback((call) => {
    if (!call?.call_id) return null;
    return `/api/admin/call-logs/recording/${encodeURIComponent(call.call_id)}`;
  }, []);

  // Memorizar la URL de grabación para la llamada seleccionada
  const recordingUrl = useMemo(() => {
    if (!selectedCall) return null;
    return getRecordingUrl(selectedCall);
  }, [selectedCall, getRecordingUrl]);

  // Verificar si existe grabación
  const hasRecording = (call) => {
    return Boolean(call?.call_id);
  };

  const filteredCallLogs = useMemo(() => {
    if (!Object.keys(columnFilters).length) {
      return callLogs;
    }
    return callLogs.filter((log) =>
      Object.entries(columnFilters).every(([columnId, filterValue]) => {
        if (!filterValue) return true;
        const normalizedFilter = String(filterValue).toLowerCase();
        const value = getColumnFilterValue(log, columnId).toLowerCase();
        return value.includes(normalizedFilter);
      })
    );
  }, [callLogs, columnFilters]);

  const sortedCallLogs = useMemo(() => {
    if (!sortConfig?.column) return filteredCallLogs;
    const sorted = [...filteredCallLogs];
    const directionFactor = sortConfig.direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      const aValue = getColumnSortValue(a, sortConfig.column);
      const bValue = getColumnSortValue(b, sortConfig.column);

      if (aValue === bValue) return 0;
      if (aValue === undefined || aValue === null) return 1 * directionFactor;
      if (bValue === undefined || bValue === null) return -1 * directionFactor;

      if (typeof aValue === 'string' || typeof bValue === 'string') {
        return aValue.toString().localeCompare(bValue.toString()) * directionFactor;
      }

      return (aValue > bValue ? 1 : -1) * directionFactor;
    });

    return sorted;
  }, [filteredCallLogs, sortConfig]);

  const displayedLogs = sortedCallLogs;
  // Cargar datos
  const fetchCallLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== '' && value !== null) {
            if (key === 'dateFrom' || key === 'dateTo') {
              acc[key] = format(value, 'yyyy-MM-dd HH:mm:ss');
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {})
      };

      const qs = new URLSearchParams(params).toString();
      const response = await apiCall(`/call-logs${qs ? `?${qs}` : ''}`);

      if (response.success) {
        setCallLogs(response.data || []);
        setStats(response.stats);
        // setFilterValues(response.filters);
        setTotalCount(response.pagination.total || 0);
      }
    } catch (error) {
      showError('Error al cargar los registros de llamadas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, rowsPerPage, filters, showError]);

  // Cargar datos inicialmente y cuando cambie la paginación
  useEffect(() => {
    fetchCallLogs();
  }, [page, rowsPerPage]); // Removido fetchCallLogs y filters de las dependencias

  // Contar filtros activos
  useEffect(() => {
    const count = Object.entries(filters).filter(([key, value]) => {
      if (key === 'sortBy' || key === 'sortOrder') return false;
      return value !== '' && value !== null;
    }).length;
    setActiveFiltersCount(count);
  }, [filters]);

  useEffect(() => {
    if (!modalOpen || !selectedCall?.call_id) {
      setTranscriptData(null);
      setTranscriptError('');
      setTranscriptLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const fetchTranscript = async () => {
      try {
        setTranscriptLoading(true);
        setTranscriptError('');
        const token = localStorage.getItem('cti_admin_token');
        const response = await fetch(
          `/api/admin/transcription/${encodeURIComponent(selectedCall.call_id)}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: 'include',
            signal: controller.signal
          }
        );

        if (controller.signal.aborted || cancelled) return;

        if (response.status === 404) {
          setTranscriptData(null);
          setTranscriptError('');
          return;
        }

        if (!response.ok) {
          throw new Error('Error al obtener transcripción');
        }

        const payload = await response.json();
        if (!cancelled) {
          setTranscriptData(payload?.data || payload);
        }
      } catch (error) {
        if (controller.signal.aborted || cancelled) return;
        console.error('[TRANSCRIPTION] Error fetching transcript:', error);
        setTranscriptError('No se pudo cargar la transcripción');
      } finally {
        if (!cancelled) {
          setTranscriptLoading(false);
        }
      }
    };

    fetchTranscript();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [modalOpen, selectedCall?.call_id]);

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (newValue) => {
    setFilters(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchCallLogs();
  };

  const clearFilters = () => {
    const { start, end } = getDefaultDateRange();
    setFilters({
      dateFrom: start,
      dateTo: end,
      agent: '',
      typecall: '',
      skill: '',
      numbercall: '',
      trunk: '',
      call_id: '',
      resultdesc: '',
      hangupcall: '',
      minDuration: '',
      maxDuration: '',
      sortBy: 'fecha',
      sortOrder: 'DESC'
    });
    setPage(0);
  };

  const handleExport = async (exportFormat = 'csv') => {
    try {
      const params = {
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== '' && value !== null) {
            if (key === 'dateFrom' || key === 'dateTo') {
              acc[key] = format(value, 'yyyy-MM-dd HH:mm:ss');
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {}),
        format: exportFormat
      };

      const qs = new URLSearchParams(params).toString();
      const token = localStorage.getItem('cti_admin_token');

      const response = await fetch(`/api/admin/call-logs/export${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Error al exportar los datos');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = exportFormat === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `call_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSuccess(`Datos exportados correctamente ${exportFormat === 'excel' ? '(Excel)' : '(CSV)'}`);
    } catch (error) {
      showError('Error al exportar los datos');
      console.error(error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Simple AudioPlayer component
  const AudioPlayer = ({ src }) => {
    const [audioSrc, setAudioSrc] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
      if (!src) {
        setAudioSrc('');
        setError('');
        return;
      }

      const loadAudio = async () => {
        try {
          setLoading(true);
          setError('');

          const token = localStorage.getItem('cti_admin_token');
          const response = await fetch(src, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: 'include'
          });

          if (!response.ok) {
            if (response.status === 404) {
              throw new Error('Grabación no encontrada');
            }
            throw new Error(`Error ${response.status}: No se pudo cargar el audio`);
          }

          const blob = await response.blob();
          const audioUrl = URL.createObjectURL(blob);
          setAudioSrc(audioUrl);
        } catch (err) {
          console.warn('[AUDIO] Error loading recording:', err);
          setError(err.message || 'No se pudo cargar la grabación');
        } finally {
          setLoading(false);
        }
      };

      loadAudio();

      return () => {
        if (audioSrc) {
          URL.revokeObjectURL(audioSrc);
        }
      };
    }, [src]);

    if (loading) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Cargando grabación...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">{error}</Alert>
      );
    }

    if (!audioSrc) {
      return (
        <Typography variant="body2" color="text.secondary">
          No hay grabación disponible
        </Typography>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        <audio
          src={audioSrc}
          controls
          style={{ width: '100%' }}
          preload="metadata"
        >
          Tu navegador no soporta el elemento de audio.
        </audio>
      </Box>
    );
  };

  const InfoSection = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Stack spacing={1.2}>{children}</Stack>
    </Paper>
  );

  const InfoRow = ({ label, value }) => (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {React.isValidElement(value) ? (
        <Box sx={{ textAlign: 'right' }}>{value}</Box>
      ) : (
        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right' }}>
          {value ?? '-'}
        </Typography>
      )}
    </Stack>
  );

  const renderTranscriptContent = () => {
    if (transcriptLoading) {
      return <LinearProgress />;
    }

    if (transcriptError) {
      return <Alert severity="error">{transcriptError}</Alert>;
    }

    const fragments = transcriptData?.transcript?.fragments || [];
    if (!fragments.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No hay transcripción disponible para esta llamada.
        </Typography>
      );
    }

    const transcriptStart = transcriptData?.transcript?.startedAt || transcriptData?.startedAt || null;
    const transcriptStartMs =
      typeof transcriptStart === 'number'
        ? transcriptStart
        : transcriptStart
          ? new Date(transcriptStart).getTime()
          : null;

    // Filtrar fragmentos que tienen texto válido
    const fragmentsWithText = fragments.filter(fragment =>
      fragment.text && fragment.text.trim().length > 0
    );

    if (fragmentsWithText.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No hay transcripción con texto reconocido para esta llamada.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
        {fragmentsWithText.map((fragment, index) => {
          const isAgent = fragment.role === 'agent';
          const fragmentStartValue = fragment.startTimestamp;
          const fragmentStartMs =
            typeof fragmentStartValue === 'number'
              ? fragmentStartValue
              : fragmentStartValue
                ? new Date(fragmentStartValue).getTime()
                : null;
          const offsetSeconds =
            transcriptStartMs !== null && fragmentStartMs !== null
              ? (fragmentStartMs - transcriptStartMs) / 1000
              : null;
          const timestampLabel =
            fragmentStartMs !== null && !Number.isNaN(fragmentStartMs)
              ? format(new Date(fragmentStartMs), 'HH:mm:ss')
              : null;

          return (
            <Stack
              // eslint-disable-next-line react/no-array-index-key
              key={`transcript-fragment-${index}`}
              alignItems={isAgent ? 'flex-end' : 'flex-start'}
            >
              <Paper
                elevation={0}
                sx={(theme) => ({
                  px: 1.5,
                  py: 1,
                  maxWidth: '85%',
                  borderRadius: 2,
                  backgroundColor: isAgent
                    ? theme.palette.primary.light
                    : theme.palette.grey[100],
                  color: isAgent
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.primary,
                  border: '1px solid transparent'
                })}
              >
                <Stack spacing={0.5}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {isAgent ? 'Agente' : 'Cliente'} {timestampLabel ? `• ${timestampLabel}` : ''}
                  </Typography>
                  <Typography variant="body2">
                    {fragment.text}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 64px)',
          position: 'relative',
          overflow: 'hidden',
          px: 0
        }}
      >
        {/* Contenido principal PRIMERO */}
        <Box sx={{
          flex: 1,
          px: 0,
          py: 1,
          overflow: 'auto',
          minWidth: 0, // Importante: previene que el flex item crezca más allá del contenedor
          transition: theme => theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          })
        }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {!drawerOpen && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={() => setDrawerOpen(true)}
                  endIcon={
                    activeFiltersCount > 0 && (
                      <Chip
                        size="small"
                        label={activeFiltersCount}
                        color="primary"
                        sx={{ height: 16, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )
                  }
                >
                  Filtros
                </Button>
              )}
              {canAction('export_call_logs') && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('csv')}
                  disabled={loading}
                >
                  CSV
                </Button>
              )}
              {canAction('export_call_logs') && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={loading}
                >
                  Excel
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchCallLogs}
                disabled={loading}
              >
                Actualizar
              </Button>
            </Stack>
          </Box>

          {/* Estadísticas */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Total de llamadas
                    </Typography>
                    <Typography variant="h5">
                      {stats.total_calls || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Duración promedio
                    </Typography>
                    <Typography variant="h5">
                      {formatDuration(stats.avg_duration)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Tiempo promedio en cola
                    </Typography>
                    <Typography variant="h5">
                      {formatDuration(stats.avg_queue_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Tiempo promedio ACW
                    </Typography>
                    <Typography variant="h5">
                      {formatDuration(stats.avg_acw_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tabla de llamadas */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && (
              <>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 10
                    }}
                  >
                    <Tooltip title="Control de zoom">
                      <IconButton
                        size="small"
                        onClick={(event) => setZoomAnchorEl(event.currentTarget)}
                        sx={{
                          backgroundColor: 'background.paper',
                          boxShadow: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 0,
                      minWidth: '100%',
                      overflowX: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        minWidth: 'max-content',
                        transform: `scale(${tableZoom / 100})`,
                        transformOrigin: 'top left',
                        width: `${(100 / tableZoom) * 100}%`,
                        '& .MuiTableCell-root': {
                          whiteSpace: 'nowrap'
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {TABLE_COLUMNS.map((column) => (
                            <TableCell key={column.id} align={column.align}>
                              <Stack spacing={0.5}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  {column.sortable ? (
                                    <TableSortLabel
                                      active={sortConfig.column === column.id}
                                      direction={sortConfig.column === column.id ? sortConfig.direction : 'asc'}
                                      onClick={() => handleSortChange(column.id)}
                                    >
                                      {column.label}
                                    </TableSortLabel>
                                  ) : (
                                    <Typography variant="subtitle2">{column.label}</Typography>
                                  )}
                                  {column.filterable && (
                                    <IconButton
                                      size="small"
                                      color={columnFilters[column.id] ? 'primary' : 'default'}
                                      onClick={() => handleToggleColumnFilter(column.id)}
                                    >
                                      <FilterListIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>
                                {column.filterable && activeFilterColumn === column.id && (
                                  <TextField
                                    value={columnFilters[column.id] || ''}
                                    onChange={(event) =>
                                      handleColumnFilterChange(column.id, event.target.value)
                                    }
                                    size="small"
                                    placeholder="Filtrar"
                                    variant="standard"
                                    autoFocus
                                    onBlur={() => setActiveFilterColumn(null)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Escape') {
                                        event.preventDefault();
                                        setActiveFilterColumn(null);
                                      }
                                    }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedLogs.map((log) => (
                          <TableRow key={log.idlog_calls} hover>
                            <TableCell>
                              {log.fecha ? format(new Date(log.fecha), 'dd/MM/yyyy HH:mm:ss') : '-'}
                            </TableCell>
                            <TableCell>{log.agent || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={log.typecall || '-'}
                                size="small"
                                color={
                                  log.typecall === 'INBOUND'
                                    ? 'success'
                                    : log.typecall === 'OUTBOUND'
                                      ? 'primary'
                                      : 'default'
                                }
                                sx={{ fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell>{log.skill || '-'}</TableCell>
                            <TableCell>{log.numbercall || '-'}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Duración de llamada">
                                <span>{formatDuration(log.timecall)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Tiempo en cola">
                                <span>{formatDuration(log.timequeue)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Tiempo de timbre">
                                <span>{formatDuration(log.timering)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="After Call Work">
                                <span>{formatDuration(log.timeacw)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Tiempo en espera">
                                <span>{formatDuration(log.timehold)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{log.resultdesc || '-'}</TableCell>
                            <TableCell>{log.trunk || '-'}</TableCell>
                            <TableCell>
                              <Tooltip title={log.call_id}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {log.call_id ? `${log.call_id.substring(0, 8)}...` : '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              {formatHangupcall(log.hangupcall)}
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <Tooltip title="Ver detalle">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDetails(log)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {hasRecording(log) && (
                                  <Tooltip title="Reproducir grabación">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleViewDetails(log)}
                                    >
                                      <PlayArrowIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                        {displayedLogs.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={TABLE_COLUMNS.length} align="center" sx={{ py: 3 }}>
                              <Typography variant="body2" color="textSecondary">
                                No se encontraron registros
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  labelRowsPerPage="Registros por página"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </>
            )}
          </Paper>

          <Popover
            open={Boolean(zoomAnchorEl)}
            anchorEl={zoomAnchorEl}
            onClose={() => setZoomAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <Box sx={{ p: 2, minWidth: 220 }}>
              <Typography variant="subtitle2" gutterBottom>
                Control de zoom
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <IconButton
                    size="small"
                    onClick={handleZoomOut}
                    disabled={tableZoom <= 50}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
                    {tableZoom}%
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleZoomIn}
                    disabled={tableZoom >= 150}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={handleZoomReset}
                  disabled={tableZoom === 100}
                >
                  Restablecer (100%)
                </Button>
              </Stack>
            </Box>
          </Popover>
        </Box>

        {/* Panel de filtros - flotante cuando está colapsado */}
        {drawerExpanded ? (
          // Panel expandido - ocupa espacio como drawer
          <Drawer
            variant="persistent"
            anchor="right"
            open={drawerOpen}
            sx={{
              width: drawerOpen ? DRAWER_WIDTH : 0,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                position: 'relative',
                height: '100%',
                borderLeft: 1,
                borderColor: 'divider'
              }
            }}
          >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header del panel - siempre visible */}
            <Box sx={{ p: 2, borderBottom: drawerExpanded ? 1 : 0, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                    Filtros
                  </Typography>
                  {activeFiltersCount > 0 && (
                    <Chip
                      size="small"
                      label={activeFiltersCount}
                      color="primary"
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => setDrawerExpanded(!drawerExpanded)}
                  >
                    {drawerExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Stack>
              </Stack>
            </Box>

            {/* Contenido de filtros - solo visible cuando está expandido */}
            {drawerExpanded && (
              <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>

            <Stack spacing={2}>
              {/* Filtros de fecha */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Rango de fechas
              </Typography>

              <DateTimePicker
                label="Fecha desde"
                value={filters.dateFrom}
                onChange={handleDateChange('dateFrom')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: FILTER_CONTROL_SX
                  }
                }}
              />

              <DateTimePicker
                label="Fecha hasta"
                value={filters.dateTo}
                onChange={handleDateChange('dateTo')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: FILTER_CONTROL_SX
                  }
                }}
              />

              <Divider />

              {/* Filtros de identificación */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Identificación
              </Typography>

              <TextField
                label="Agente"
                value={filters.agent}
                onChange={handleFilterChange('agent')}
                fullWidth
                size="small"
                sx={FILTER_CONTROL_SX}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                label="ID de llamada"
                value={filters.call_id}
                onChange={handleFilterChange('call_id')}
                fullWidth
                size="small"
                sx={FILTER_CONTROL_SX}
              />

              <TextField
                label="Número"
                value={filters.numbercall}
                onChange={handleFilterChange('numbercall')}
                fullWidth
                size="small"
                sx={FILTER_CONTROL_SX}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <Divider />

              {/* Filtros de categorización */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Categorización
              </Typography>

              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel>Tipo de llamada</InputLabel>
                <Select
                  value={filters.typecall}
                  onChange={handleFilterChange('typecall')}
                  label="Tipo de llamada"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="INBOUND">Entrante</MenuItem>
                  <MenuItem value="OUTBOUND">Saliente</MenuItem>
                  <MenuItem value="INTERNAL">Interna</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Skill"
                value={filters.skill}
                onChange={handleFilterChange('skill')}
                fullWidth
                size="small"
                sx={FILTER_CONTROL_SX}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                label="Trunk"
                value={filters.trunk}
                onChange={handleFilterChange('trunk')}
                fullWidth
                size="small"
                sx={FILTER_CONTROL_SX}
              />

              <TextField
                label="Resultado"
                value={filters.resultdesc}
                onChange={handleFilterChange('resultdesc')}
                fullWidth
                size="small"
                sx={FILTER_CONTROL_SX}
              />

              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel>Quién colgó</InputLabel>
                <Select
                  value={filters.hangupcall}
                  onChange={handleFilterChange('hangupcall')}
                  label="Quién colgó"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="1">Agente (1)</MenuItem>
                  <MenuItem value="0">Usuario (0)</MenuItem>
                </Select>
              </FormControl>

              <Divider />

              {/* Filtros de duración */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Duración (segundos)
              </Typography>

              <Stack direction="row" spacing={1}>
                <TextField
                  label="Mín"
                  type="number"
                  value={filters.minDuration}
                  onChange={handleFilterChange('minDuration')}
                  size="small"
                  sx={FILTER_CONTROL_SX}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  label="Máx"
                  type="number"
                  value={filters.maxDuration}
                  onChange={handleFilterChange('maxDuration')}
                  size="small"
                  sx={FILTER_CONTROL_SX}
                />
              </Stack>

              <Divider />

              {/* Ordenamiento */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Ordenamiento
              </Typography>

              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.sortBy}
                  onChange={handleFilterChange('sortBy')}
                  label="Ordenar por"
                >
                  <MenuItem value="fecha">Fecha</MenuItem>
                  <MenuItem value="agent">Agente</MenuItem>
                  <MenuItem value="timecall">Duración</MenuItem>
                  <MenuItem value="timequeue">Tiempo en cola</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel>Dirección</InputLabel>
                <Select
                  value={filters.sortOrder}
                  onChange={handleFilterChange('sortOrder')}
                  label="Dirección"
                >
                  <MenuItem value="ASC">Ascendente</MenuItem>
                  <MenuItem value="DESC">Descendente</MenuItem>
                </Select>
              </FormControl>

              <Divider />

              {/* Botón aplicar filtros */}
              <Button
                variant="contained"
                fullWidth
                onClick={handleApplyFilters}
                startIcon={<FilterListIcon />}
              >
                Aplicar Filtros
              </Button>
            </Stack>
              </Box>
            )}
          </Box>
          </Drawer>
        ) : (
          // Panel colapsado - flotante, no ocupa espacio
          <Paper
            sx={{
              position: 'absolute',
              right: 20,
              top: 20,
              width: 'auto',
              minWidth: 150,
              zIndex: 1200,
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}
          >
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                    Filtros
                  </Typography>
                  {activeFiltersCount > 0 && (
                    <Chip
                      size="small"
                      label={activeFiltersCount}
                      color="primary"
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setDrawerExpanded(!drawerExpanded)}
                >
                  {drawerExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Modal de Detalle de Llamada */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Detalle de Llamada</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCall && (
            <Stack spacing={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoSection title="Información Básica">
                    <InfoRow
                      label="Fecha y Hora"
                      value={
                        selectedCall.fecha
                          ? format(new Date(selectedCall.fecha), 'dd/MM/yyyy HH:mm:ss')
                          : '-'
                      }
                    />
                    <InfoRow label="Agente" value={selectedCall.agent || '-'} />
                    <InfoRow
                      label="Tipo de Llamada"
                      value={
                        <Chip
                          label={selectedCall.typecall || '-'}
                          size="small"
                          color={
                            selectedCall.typecall === 'INBOUND'
                              ? 'success'
                              : selectedCall.typecall === 'OUTBOUND'
                                ? 'primary'
                                : 'default'
                          }
                        />
                      }
                    />
                    <InfoRow label="Número" value={selectedCall.numbercall || '-'} />
                    <InfoRow label="Skill" value={selectedCall.skill || '-'} />
                    <InfoRow label="Resultado" value={selectedCall.resultdesc || '-'} />
                  </InfoSection>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoSection title="Información Técnica">
                    <InfoRow
                      label="Call ID"
                      value={
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {selectedCall.call_id || '-'}
                        </Typography>
                      }
                    />
                    <InfoRow label="Trunk" value={selectedCall.trunk || '-'} />
                    <InfoRow
                      label="Quién colgó"
                      value={formatHangupcall(selectedCall.hangupcall)}
                    />
                  </InfoSection>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoSection title="Tiempos">
                    <InfoRow label="Duración de Llamada" value={formatDuration(selectedCall.timecall)} />
                    <InfoRow label="Tiempo en Cola" value={formatDuration(selectedCall.timequeue)} />
                    <InfoRow label="Tiempo de Ring" value={formatDuration(selectedCall.timering)} />
                    <InfoRow label="Tiempo ACW" value={formatDuration(selectedCall.timeacw)} />
                    <InfoRow label="Tiempo Hold" value={formatDuration(selectedCall.timehold)} />
                  </InfoSection>
                </Grid>

                {hasRecording(selectedCall) && canAction('play_recording_call_logs') && (
                  <Grid item xs={12}>
                    <InfoSection title="Grabación">
                      <AudioPlayer src={recordingUrl} />
                    </InfoSection>
                  </Grid>
                )}
              </Grid>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Transcripción
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, minHeight: 160 }}>
                  {renderTranscriptContent()}
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CallLogsPage;
